import { type ReactNode } from "react";
import { GuideButton } from "@/features/page-guide/GuideButton";
import { BRAND_ART, BrandMark } from "./BrandArt";
import { HintTip } from "./HintTip";

type Props = {
  title: ReactNode;
  /**
   * Lời giới thiệu trang — thu vào dấu "!" cạnh tiêu đề (CEO chốt 2a,
   * 28/10/2026). Câu người dùng PHẢI thấy ngay (không có quyền, lỗi, số đơn,
   * cơ sở · ngày đang xem) thì truyền qua `note`.
   */
  description?: string;
  /** Dòng phụ luôn hiện dưới tiêu đề: trạng thái, lỗi, ngữ cảnh đang xem. */
  note?: ReactNode;
  actions?: ReactNode;
  /**
   * Bài hướng dẫn cần mở, khi đường dẫn không tự suy ra được (trang lồng, hoặc
   * trang muốn mượn bài của trang khác). Bỏ trống là tự suy từ đường dẫn.
   */
  guideId?: string | undefined;
  /**
   * Thẻ đang mở, với trang có hộp hướng dẫn chia theo thẻ — hộp mở thẳng vào
   * bài của thẻ đó thay vì bắt người đọc đi tìm.
   *
   * Phải truyền TAY chứ không suy từ đường dẫn: trang Thanh toán không khai
   * `validateSearch` cho route và tự ghi `?tab=` bằng `history.replaceState`,
   * nên bộ định tuyến không thấy thẻ nào cả. Trang nào có thẻ nằm trong đường
   * dẫn thì khỏi truyền, nút tự suy được.
   */
  guideTab?: string | undefined;
  /** Tắt nút hướng dẫn ở màn không cần (trang công khai, màn kỹ thuật). */
  hideGuide?: boolean | undefined;
};

export function PageHeader({
  title,
  description,
  note,
  actions,
  guideId,
  guideTab,
  hideGuide,
}: Props) {
  return (
    <div className="relative mb-4 flex min-w-0 flex-col gap-2 pb-3 md:mb-6 md:flex-row md:items-start md:justify-between md:gap-4">
      {/* Gold hairline fading out to the right (mockup 28/09/2026). */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-px"
        style={{ background: "linear-gradient(90deg, var(--gn-gold), transparent 70%)" }}
      />
      <div className="min-w-0">
        {/* Brand serif only for plain-text titles: titles that carry badges or
            icons keep the body font so status chips don't change look. */}
        <div className="flex min-w-0 items-center gap-2">
          {typeof title === "string" ? (
            <h1
              className="flex items-center gap-2.5 text-[22px] font-normal leading-tight md:text-[26px] lg:text-[30px]"
              style={{ fontFamily: "var(--font-art)" }}
            >
              <BrandMark
                src={BRAND_ART.flower}
                className="h-3.5 w-3.5 shrink-0 text-[var(--gn-gold)] md:h-4 md:w-4"
              />
              {title}
            </h1>
          ) : (
            <h1 className="text-lg font-semibold md:text-xl lg:text-2xl">{title}</h1>
          )}
          {description ? (
            <HintTip label="Giới thiệu trang" align="start">
              {description}
            </HintTip>
          ) : null}
        </div>
        {note ? <p className="text-sm text-muted">{note}</p> : null}
      </div>
      {/* Nút hướng dẫn đứng cùng hàng với các nút thao tác, ở SAU chúng: việc
          chính của trang phải nằm chỗ tay quen bấm, hướng dẫn là thứ thỉnh
          thoảng mới cần. Trang chưa có bài thì GuideButton tự ẩn, nên hàng nút
          không đội thêm khoảng trống. */}
      {(actions || !hideGuide) && (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 md:w-auto">
          {actions}
          {!hideGuide && <GuideButton guideId={guideId} guideTab={guideTab} />}
        </div>
      )}
    </div>
  );
}
